import { useState, type ReactNode } from 'react';

// Shared filter row used by Dashboard / Responses / Inbox. Date range,
// survey selector, dock selector. Emits a flat object of query params via
// onChange so callers can pass straight to the metrics endpoints.

export type FiltersValue = {
  from: string; // YYYY-MM-DD
  to: string;
  survey_key: string; // '' = all
  dock_id: string;    // '' = all
};

export const SURVEY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '',              label: 'All surveys' },
  { value: 'post_sale',     label: 'Post-sale' },
  { value: 'post_trip',     label: 'Post-trip' },
  { value: 'post_training', label: 'Post-training' },
];

export type DockOption = { id: string; name: string };

export function defaultFilters(): FiltersValue {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 24 * 3600 * 1000);
  return {
    from: from.toISOString().slice(0, 10),
    to:   to.toISOString().slice(0, 10),
    survey_key: '',
    dock_id: '',
  };
}

export function buildQueryString(v: FiltersValue): string {
  const params = new URLSearchParams();
  if (v.from) params.set('from', v.from);
  if (v.to)   params.set('to', v.to);
  if (v.survey_key) params.set('survey_key', v.survey_key);
  if (v.dock_id)    params.set('dock_id', v.dock_id);
  return params.toString();
}

export function FilterBar(props: {
  value: FiltersValue;
  onChange: (next: FiltersValue) => void;
  docks: DockOption[];
  rightSlot?: ReactNode;
}): JSX.Element {
  const { value, onChange, docks, rightSlot } = props;
  return (
    <div className="flex flex-wrap items-end gap-3 mb-6 p-4 bg-white border border-gray-200 rounded-lg">
      <Field label="From">
        <input
          type="date"
          value={value.from}
          onChange={(e) => onChange({ ...value, from: e.target.value })}
          className="border border-gray-300 rounded px-2 py-1 text-sm"
        />
      </Field>
      <Field label="To">
        <input
          type="date"
          value={value.to}
          onChange={(e) => onChange({ ...value, to: e.target.value })}
          className="border border-gray-300 rounded px-2 py-1 text-sm"
        />
      </Field>
      <Field label="Survey">
        <select
          value={value.survey_key}
          onChange={(e) => onChange({ ...value, survey_key: e.target.value })}
          className="border border-gray-300 rounded px-2 py-1 text-sm"
        >
          {SURVEY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="Dock">
        <select
          value={value.dock_id}
          onChange={(e) => onChange({ ...value, dock_id: e.target.value })}
          className="border border-gray-300 rounded px-2 py-1 text-sm"
        >
          <option value="">All docks</option>
          {docks.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </Field>
      {rightSlot && <div className="ml-auto">{rightSlot}</div>}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
      {children}
    </label>
  );
}

// Simple uncontrolled wrapper: pages that just want defaults + a setter.
export function useFilters(): [FiltersValue, (next: FiltersValue) => void] {
  const [value, setValue] = useState<FiltersValue>(defaultFilters());
  return [value, setValue];
}
